import React, {
  Fragment,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import useOnEscapeKeyDown from 'shared/hooks/onEscapeKeyDown';
import logger from 'utils/logger';
import {
  ScrollOverlay,
  ClickableOverlay,
  StyledModal,
  CloseIcon,
} from './Styles';
import { ThemeProvider } from '@mui/material/styles';
import { createLanguageAwareTheme } from 'utils/themeFactory';
import { useMaterialUIController } from 'context';

const log = logger.createLogger('Modal');

const propTypes = {
  testid: PropTypes.string,
  height: PropTypes.string,
  minHeight: PropTypes.string,
  width: PropTypes.string,
  minWidth: PropTypes.string,
  withCloseIcon: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  renderLink: PropTypes.func,
  renderContent: PropTypes.func.isRequired,
  isPrevIssue: PropTypes.bool,
  zIndex: PropTypes.number,
};

const Modal = ({
  testid = 'modal',
  height = undefined,
  minHeight = undefined,
  width = undefined,
  minWidth = undefined,
  withCloseIcon,
  isOpen: propsIsOpen,
  onClose: tellParentToClose,
  renderLink,
  renderContent,
  isPrevIssue,
  zIndex,
}) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [stateIsOpen, setStateOpen] = useState(false);
  const isControlled = typeof propsIsOpen === 'boolean';
  const isOpen = isControlled ? propsIsOpen : stateIsOpen;

  const theme = createLanguageAwareTheme(darkMode);
  const $modalRef = useRef();
  const $clickableOverlayRef = useRef();

  const closeModal = useCallback(() => {
    if (!isControlled) {
      setStateOpen(false);
    } else {
      tellParentToClose();
    }
  }, [isControlled, tellParentToClose]);

  useOnEscapeKeyDown(isOpen, closeModal);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'visible';
    };
  }, [isOpen]);
  return (
    <ThemeProvider theme={theme}>
      <Fragment>
        {!isControlled && renderLink({ open: () => setStateOpen(true) })}

        {isOpen &&
          ReactDOM.createPortal(
            <ScrollOverlay data-testid="Modal-ScrollOverlay" zIndex={zIndex}>
              <ClickableOverlay
                data-testid="Modal-ClickableOverlay"
                ref={$clickableOverlayRef}
                zIndex={zIndex}>
                <StyledModal
                  isPrevIssue={isPrevIssue}
                  height={height}
                  minHeight={minHeight}
                  width={width}
                  minWidth={minWidth}
                  data-testid={testid}
                  ref={$modalRef}
                  zIndex={zIndex}>
                  {withCloseIcon && (
                    <CloseIcon
                      type="close"
                      variant="center"
                      onClick={closeModal}
                    />
                  )}
                  {renderContent({ close: closeModal })}
                </StyledModal>
              </ClickableOverlay>
            </ScrollOverlay>,
            $root
          )}
      </Fragment>
    </ThemeProvider>
  );
};

const $root = document.getElementById('root');

Modal.propTypes = propTypes;

export default Modal;
